import restClient from 'restClient'
import { generateUUID } from 'util/misc'
import { BomGeneratorAbstract } from '../../bom_generators/BomGeneratorAbstract'
import { getSystemRoofType } from '../../compatibility'
import { BasicSpacingLayoutAdjusterAbstract } from '../../layout_adjusters/BasicSpacingLayoutAdjusterAbstract'
import { MountingSystemAbstract } from '../../MountingSystemAbstract'
import {
  BomGeneratorContext,
  CompatibilityParameters,
  MountingCalcResult,
  MountingInputsIntegrationData,
  PanelBlock,
  RoofTypeName,
} from '../../types'
import { K2SchemaMap } from './K2APISchema'
import {
  generateCompatibilityRules,
  generateFilterCriteria,
  roofTypesCompatibleWithK2Crossrail,
  roofTypesCompatibleWithK2SimpleTilt,
} from './K2Compatibility'
import { getK2Config } from './K2Config'
import { autoFetchDlubalDataIfNeeded } from './K2Dlubal'
import {
  getSystemRoofMaterial,
  getTiltAngleBasedOnRoofShape,
  metersToInches,
  railSystemToEndpointPathSegment,
} from './K2HelperFunctions'
import { K2PartsMap } from './K2PartsList'
import { CustomModuleUnit, K2CrossRailOutput, K2InputMap, K2MountingSystemName, K2RailType } from './K2Types'
export class K2OnRoof extends MountingSystemAbstract {
  layoutAdjusterClass = K2LayoutAdjuster
  bomGeneratorClass = K2OnRoofBomGenerator
  getCompatibilityParameters(): CompatibilityParameters {
    return {
      roofTypeRequired: true,
      roofTypes: roofTypesCompatibleWithK2Crossrail,
    }
  }
}

export class K2SimpleTilt extends MountingSystemAbstract {
  layoutAdjusterClass = K2LayoutAdjuster
  bomGeneratorClass = K2SimpleTiltBomGenerator
  getCompatibilityParameters(): CompatibilityParameters {
    return {
      roofTypeRequired: true,
      roofTypes: roofTypesCompatibleWithK2SimpleTilt,
      slopeRange: [0, 5],
      relativeTiltsIdentical: true,
      relativeTiltsAllowedValues: [0, 10, 15, 25],
      panelOrientation: 'portrait',
    }
  }
}

export class K2GroundMount extends MountingSystemAbstract {
  layoutAdjusterClass = K2LayoutAdjuster
  bomGeneratorClass = K2GroundMountBomGenerator
  getCompatibilityParameters(): CompatibilityParameters {
    return {
      roofTypeRequired: true,
    }
  }
}

class K2LayoutAdjuster extends BasicSpacingLayoutAdjusterAbstract {
  spacingRuleSet = {
    interPanel: { horizontalSpacing: 12, verticalSpacing: 12 },
  }
}

const latestGenerationIdBySystem = new Map<string, string>()
class K2GroundMountBomGenerator extends BomGeneratorAbstract {
  async generateBom(mountingCalcResult: MountingCalcResult, context: BomGeneratorContext): Promise<MountingCalcResult> {
    return generateBomWithK2USAPI('k2GroundMount', mountingCalcResult, this.input.options, context, this.block.uuid)
  }
}

class K2SimpleTiltBomGenerator extends BomGeneratorAbstract {
  async generateBom(mountingCalcResult: MountingCalcResult, context: BomGeneratorContext): Promise<MountingCalcResult> {
    return generateBomWithK2USAPI('k2SimpleTilt', mountingCalcResult, this.input.options, context, this.block.uuid)
  }
}

class K2OnRoofBomGenerator extends BomGeneratorAbstract {
  async generateBom(mountingCalcResult: MountingCalcResult, context: BomGeneratorContext): Promise<MountingCalcResult> {
    return generateBomWithK2USAPI('k2OnRoof', mountingCalcResult, this.input.options, context, this.block.uuid)
  }
}

async function generateBomWithK2USAPI(
  k2MountingSystemName: K2MountingSystemName,
  mountingCalcResult: MountingCalcResult,
  inputOptions: MountingInputsIntegrationData,
  context: BomGeneratorContext,
  blockId: string
): Promise<MountingCalcResult> {
  // Ensure that the BOM generation is not duplicated if user toggles dropdown while waiting for Dlubal
  const generationId = generateUUID()
  latestGenerationIdBySystem.set(blockId, generationId)

  // Auto-fetch Dlubal data if wind/snow values are not already provided
  await autoFetchDlubalDataIfNeeded(inputOptions)

  if (latestGenerationIdBySystem.get(blockId) !== generationId) {
    return mountingCalcResult
  }

  try {
    const railSystem =
      k2MountingSystemName === 'k2OnRoof'
        ? 'CROSSRAIL'
        : k2MountingSystemName === 'k2SimpleTilt'
        ? 'SIMPLE_TILT_US'
        : 'GROUND'
    if (!railSystem) throw Error('No rail system')
    const k2RailType = railSystemToEndpointPathSegment(railSystem)
    const k2Input = generateK2Inputs(k2RailType, mountingCalcResult, inputOptions)
    console.log('K2 Input:\n', k2Input.input)
    console.log('========================================')
    const k2Data = await fetchK2Data(k2Input)
    if (latestGenerationIdBySystem.get(blockId) !== generationId) {
      return mountingCalcResult
    }
    const k2Bom = k2Data.data.bom as K2CrossRailOutput
    console.log('K2 BOM:', k2Bom)
    const engineering = k2Bom.engineering
    verifyBOMValidity(k2Bom)
    checkEngineering(engineering, k2Input.input.attachmentSpacing)
    mountingCalcResult.items.push(...generateK2Items(k2Bom))
  } catch (ex: any) {
    context.errorHandling.http(ex, { msg: window.translate('BOM generation failed') })
    window.Designer.showNotification(window.translate('BOM generation failed'), 'warning')
  } finally {
    return mountingCalcResult
  }
}

function verifyBOMValidity(k2Bom: K2CrossRailOutput) {
  if (k2Bom.isValid === false) {
    const translate = window.translate
    const sanitizeValue = (value: any) => {
      // Avoid showing arbitrary strings in the error message
      if (Object.keys(K2PartsMap).includes(String(value))) return String(value)
      console.warn('Unknown value for K2 BOM:', value)
      return 'unknown'
    }
    const errorParts = k2Bom.validationErrors.map((error) => sanitizeValue(error.value)).join(',')
    window.Designer.showNotification(
      translate('The following components are no longer available') + ': ' + errorParts,
      'error'
    )
    throw Error()
  }
}

function checkEngineering(engineering: any, attachmentSpacing: number) {
  if (!engineering) return
  const engineeringValues = Object.values(engineering) as any[]
  const leafValues = engineeringValues.flatMap(Object.values).flatMap(Object.values)

  const spans: number[] = []
  for (const item of leafValues) {
    if (!item || typeof item !== 'object') continue
    // CrossRail format: { maxReactions: { span: number[] } }
    if (item.maxReactions?.span != null) {
      const spanValues = Array.isArray(item.maxReactions.span) ? item.maxReactions.span : [item.maxReactions.span]
      spans.push(...spanValues.filter((s: any) => typeof s === 'number'))
    }
    // SimpleTilt format: { maxSpan: number }
    if (typeof item.maxSpan === 'number') {
      spans.push(item.maxSpan)
    }
  }

  if (spans.length === 0) return
  const minSpan = Math.min(...spans)
  console.log('Min Span:', minSpan)
  if (attachmentSpacing > minSpan) {
    const message = window.translate(`Attachment spacing needs to be at most ${minSpan} inches.`)
    window.Designer.showNotification(message, 'warning')
    throw new Error(message)
  }
}

function generateK2Items(bom: K2CrossRailOutput) {
  return Object.values(bom)
    .flat()
    .filter(({ partNumber, quantity }) => !!partNumber && partNumber !== 'TBD' && !!quantity)
    .flatMap(({ partNumber, quantity }) =>
      Array(quantity).fill({
        name: partNumber,
        components: [],
      })
    )
}

function k2BaseURL() {
  const url = 'orgs/' + window.WorkspaceHelper.project.org_id + '/k2us/bom/'
  return url
}

function fetchK2Data<T extends K2RailType>(k2Input: { k2RailType: T; input: K2InputMap[T] }) {
  const restClientInstance = restClient(window.API_ROOT + '/api')
  const { k2RailType, input } = k2Input
  const url = k2BaseURL() + k2RailType + '/'
  return restClientInstance('CUSTOM_POST', 'custom', {
    url,
    data: input,
  })
}

function panelBlockToK2RoofSelectedPanels(panelBlock: PanelBlock) {
  const panelArray = window.editor.objectByUuid(panelBlock.uuid)
  const isPortrait = panelArray.moduleLayout() === 'portrait'
  const panelGrid = panelBlock.rows
  const numRows = panelGrid.length || 0
  const numColumns = panelGrid[0].length || 0
  const selected = panelBlock.rows
    .map((row, rowIndex) => {
      return row.map((panelInfo, colIndex) => {
        return panelInfo.isActive ? `${rowIndex + 1}-${colIndex + 1}` : ''
      })
    })
    .flat(2)
    .filter((cell) => cell !== '')
    .join(',')
  const horizontalRows = isPortrait ? '' : Array.from({ length: numRows }, (_, i) => i + 1).join(',')
  const material = getSystemRoofMaterial()
  return {
    numRows,
    numColumns,
    selected,
    horizontalRows,
    material,
  }
}

function generateK2Inputs<T extends K2RailType>(
  k2RailType: T,
  mountingCalcResult: MountingCalcResult,
  inputOptions: Record<string, any>
): {
  k2RailType: T
  input: K2InputMap[T]
} {
  const moduleType = window.editor.selectedSystem.moduleType()
  const customModuleUnit = 'meters' as CustomModuleUnit
  const modulePerformance = Math.round(moduleType.kw_stc * 1000)
  const DEFAULT_MODULE_HEIGHT = 35 // mm
  const customModuleHeight = moduleType.thickness ?? DEFAULT_MODULE_HEIGHT // mm
  const [customModuleWidth, customModuleLength] = moduleType.size // meters
  const roofs = mountingCalcResult.panelBlocks.map(panelBlockToK2RoofSelectedPanels)
  const slopes = mountingCalcResult.panelBlocks.map((block) => window.editor.objectByUuid(block.uuid).getSlope())
  const customModuleTilt = mountingCalcResult.panelBlocks
    .map((block) => window.editor.objectByUuid(block.uuid).getRacksTilt())
    .map((tilt) => Number(tilt.toFixed(0)))[0]
  const averageSlope = slopes.reduce((acc, cur) => acc + cur, 0) / slopes.length
  const userInput = parseK2InputOptions(k2RailType, inputOptions)
  const userEngineeringInput = parseK2EngineeringInput(k2RailType, inputOptions)
  const engineering = {
    railType: (userInput as any).railType,
    tiltAngle: getTiltAngleBasedOnRoofShape(k2RailType, inputOptions.roofShape, averageSlope),
    panelLength: metersToInches(customModuleLength),
    attachmentSpacing: (userInput as any).attachmentSpacing,
    reactionForces: false,
    deflection: false,
    ...userEngineeringInput,
  }
  const input = {
    modulePerformance,
    region: 'US', // this will be dynamic once LATAM is rolled out
    customModuleUnit,
    customModuleHeight,
    customModuleLength,
    customModuleWidth,
    ...(k2RailType === 'simpleTilt' ? { customModuleTilt } : undefined),
    roofs,
    ...userInput,
    enforceBusinessRules: true,
    engineering,
  } as K2InputMap[T]
  return {
    k2RailType,
    input,
  }
}

function isValueCompatible(
  k2RailType: K2RailType,
  inputOptions: Record<string, any>,
  fieldName: string,
  fieldValue: any
) {
  const roofTypeName = getSystemRoofType() as RoofTypeName
  const compatibilityRules = generateCompatibilityRules(getK2Config(k2RailType).mountingSystemName)
  const filterCriteria = generateFilterCriteria(
    getK2Config(k2RailType).mountingSystemName,
    inputOptions as any,
    roofTypeName
  )
  const dropdownInputRules: any = (compatibilityRules as any)[fieldName]
  if (!dropdownInputRules) return true
  for (const criteria of filterCriteria) {
    if (!Object.prototype.hasOwnProperty.call(dropdownInputRules, criteria.filterType)) continue
    const inputOptionRules = dropdownInputRules[criteria.filterType]
    let optionValue: any = fieldValue
    if (typeof optionValue === 'boolean') optionValue = optionValue.toString()
    const rulesForProduct: string[] | undefined = inputOptionRules?.[String(optionValue)]
    const matches = rulesForProduct ? rulesForProduct.includes(String(criteria.filterValue)) : true
    if (!matches) return false
  }
  return true
}

function parseK2InputOptions<T extends K2RailType>(
  k2RailType: T,
  inputOptions: Record<string, any>
): Partial<K2InputMap[T]> {
  const schema = K2SchemaMap[k2RailType]
  const output = {}
  for (const [key, value] of Object.entries(inputOptions)) {
    const schemaProp = schema.properties[key]
    if (!schemaProp) continue
    const expectedType = schemaProp.type
    // Skip incompatible values according to current compatibility rules/criteria
    if (!isValueCompatible(k2RailType, inputOptions, key, value)) continue
    if (key === 'attachments' || key === 'endClamp' || key === 'fastener') {
      output[key] = {
        partNumber: value,
        extraQuantity: 0,
      }
    } else if (expectedType === 'string' && typeof value === 'string') {
      output[key] = value
    } else if ((expectedType === 'integer' || expectedType === 'number') && !isNaN(Number(value))) {
      output[key] = Number(value)
    }
  }
  return output
}

function parseK2EngineeringInput<T extends K2RailType>(k2RailType: T, inputOptions: Record<string, any>) {
  const schema = getK2Config(k2RailType).engineeringSchema
  const output = {}
  for (const [key, value] of Object.entries(inputOptions)) {
    const schemaProp = schema.properties[key]
    if (!schemaProp) continue
    const expectedType = schemaProp.type
    if (expectedType === 'string' && typeof value === 'string') {
      output[key] = value
    } else if ((expectedType === 'integer' || expectedType === 'number') && !isNaN(Number(value))) {
      output[key] = Number(value)
    }
  }
  return output
}
